import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CustomerService } from '../services/CustomerService';
import { normalizeMobile } from '../../../shared/utils/formatters';
import { ArrowLeft, Trash2, AlertTriangle, TrendingUp, ShoppingBag, Activity, Star, Clock, MapPin, Plus, Edit2, Landmark, Users } from 'lucide-react';
import Button from '../../../ui-kit/Button';
import RemarksSection from './RemarksSection';
import ComplaintsSection from './ComplaintsSection';
import CustomerTimeline from './CustomerTimeline';
import { CustomerComplaintsPanel } from '../../complaints';
import PartialCodStrip from '../../orders/ui/shared/PartialCodStrip';
import PaymentLinkPanel from '../../parser/ui/PaymentLinkPanel';
import CustomerEscalationsPanel from '../../escalations/ui/CustomerEscalationsPanel';
import AddOrderModal from './AddOrderModal';
import ProfileAddOrderModal from './ProfileAddOrderModal';
import { resolveAddresses, primaryAddress } from '../logic/customerAddress';
import { createOrder } from '../../orders/services/orderCreator';
import { fetchProducts } from '../../products/services/productService';
import CustomerActionBar from './CustomerActionBar';
import FollowupDueBanner from './FollowupDueBanner';
import CallLogModal from './CallLogModal';
import dialViaPhone from '../../../core/telephony/dialViaPhone';
import FeedbackCallModal from './FeedbackCallModal';
import DoNotShipModal from './DoNotShipModal';
import CustomerOrderHistoryModal from './CustomerOrderHistoryModal';
import CustomerWalletCard from '../../rewards/ui/CustomerWalletCard';
import { rewardsService } from '../../rewards/services/rewardsService';
import EditCustomerModal from './EditCustomerModal';
import EditAddressModal from './EditAddressModal';
import { ageFrom, daysToBirthday } from '../logic/customerProfileFields';
import { resolveOrderProduct } from '../logic/productName';
import { db } from '../../../integrations/firebase';
import { ref, get } from 'firebase/database';
import { TelesalesService } from '../services/telesalesService';
import { lastTelecallerRemark, isDND } from '../logic/telesales';
import { useChat } from '../../chat/hooks/useChat';
import { csvCell } from '../../../shared/utils/csv';
import { formatDate } from '../../../shared/utils/formatters';

const Section = ({ title, icon: Icon, children, className = "" }) => (
    <div className={`bg-white p-6 rounded-xl shadow-sm border border-slate-100 ${className}`}>
        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-50">
            {Icon && <Icon size={18} className="text-purple-600" />}
            <h3 className="font-bold text-slate-800">{title}</h3>
        </div>
        {children}
    </div>
);

const StatCard = ({ label, value, subtext, color = "text-slate-800" }) => (
    <div className="bg-slate-50 p-4 rounded-lg">
        <p className="text-xs text-slate-500 uppercase font-semibold">{label}</p>
        <p className={`text-xl font-bold mt-1 ${color}`}>{value}</p>
        {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
    </div>
);

const CustomerProfile = ({ customerId: propCustomerId }) => {
    const params = useParams();
    // "+ Order" on the live-call dock lands here with ?addOrder=1 so the agent
    // gets the order form itself, not the profile to then click through.
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // Prioritize prop, then param 'customerId', then param 'mobile'
    const mobile = propCustomerId || params.customerId || params.mobile;

    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
    const [orderLoading, setOrderLoading] = useState(false);
    const [lastCreatedOrder, setLastCreatedOrder] = useState(null);  // { key, order }

    // Open the order form immediately when arrived at via "+ Order", then drop
    // the flag so a refresh or a Back doesn't reopen it unexpectedly.
    useEffect(() => {
        if (searchParams.get('addOrder') !== '1') return;
        setIsOrderModalOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete('addOrder');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // Telesales workspace
    const { currentUser, allUsers } = useChat();
    // Remarks store WHO wrote them as a login id ("2gQBd4…"); show the person's name.
    // An entry that is already a name (older remarks) is shown as written.
    const staffNameFor = (who) => {
        const u = (allUsers || []).find(x => x && (x.id === who || x.uid === who));
        if (u) return u.name || u.displayName || u.email || 'Staff';
        const s = String(who || '');
        // A long id with no match is a login that no longer exists — never print the raw id.
        return s.length >= 20 && !/[\s@]/.test(s) ? 'Former staff member' : s;
    };
    const [showCall, setShowCall] = useState(false);
    const [dialing, setDialing] = useState(false);
    // null until a call is attempted; then { placed, error } so the log form can say
    // whether the system dialled or the agent is recording a call they made themselves.
    const [callAttempt, setCallAttempt] = useState(null);

    /**
     * Ring the customer on the agent's own phone, then open the result form.
     *
     * The server owns every rule here (DND, cooldown, assignment, one-call-at-a-time)
     * and answers with the reason it refused, which is shown as-is — second-guessing it
     * in the browser is how two sources of truth start to disagree.
     */
    const startCall = useCallback(async (purpose = 'manual') => {
        if (dialing) return;                       // a double-click must not dial twice
        setDialing(true);
        try {
            const number = customer?.primaryMobile || customer?.mobile || mobile;
            const res = await dialViaPhone(number, purpose);
            if (res.ok) {
                // A live call already has a home: the Live Call dock, which carries the
                // customer, hang-up, outcome and order actions. Opening the long log
                // form on top of it put two half-forms on screen for one call, so the
                // form is now offered, not forced.
                setCallAttempt({ placed: true, confirmed: !!res.confirmed, unconfirmed: !!res.unconfirmed, cancel: res.cancel });
                return;
            }
            // Refused on policy: no call was made and none should be — so no result form.
            if (res.blocked) return;
            // The dial did not get through, but the agent may have called by hand.
            setCallAttempt({ placed: false, error: res.error });
            setShowCall(true);
        } finally { setDialing(false); }
    }, [dialing, customer, mobile]);
    const [showFeedback, setShowFeedback] = useState(false);
    const [pendingCallId, setPendingCallId] = useState(null); // call→order conversion link
    const [showDoNotShip, setShowDoNotShip] = useState(false);
    const [dnsLoading, setDnsLoading] = useState(false);
    const [showOrderHistory, setShowOrderHistory] = useState(false);
    const [productsMap, setProductsMap] = useState({});
    const [reorderItems, setReorderItems] = useState(null);
    const [orderAddress, setOrderAddress] = useState(null);

    useEffect(() => { fetchProducts().then(setProductsMap).catch(() => {}); }, []);

    // Exclusive-offer / campaign status → "Offers" head on the timeline.
    const [offerRows, setOfferRows] = useState([]);
    useEffect(() => {
        let cancel = false;
        (async () => {
            if (!mobile) { setOfferRows([]); return; }
            try {
                const { authHeaders } = await import('../../../core/api/authHeaders');
                const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
                const r = await fetch(`${PROXY}/api/customers/offer-status?mobile=${encodeURIComponent(mobile)}`, { headers: await authHeaders() });
                const j = await r.json().catch(() => ({}));
                if (cancel || !j?.ok) return;
                setOfferRows((j.offers || []).map(o => {
                    const ts = o.notifiedAt ? new Date(o.notifiedAt).getTime() : (o.createdAt || Date.now());
                    if (o.purchased) return { category: 'offers', title: 'Exclusive offer redeemed 🎉', description: `${o.title} (${o.summary}) — purchased${o.amount ? ` · ₹${o.amount.toLocaleString('en-IN')}` : ''}${o.orderId ? ` · ${o.orderId}` : ''}`, ts };
                    if (o.notifiedAt) return { category: 'offers', title: 'Limited-time offer shared — not yet purchased', description: `${o.title} (${o.summary}) · sent ${o.notifiedAt}`, ts };
                    return { category: 'offers', title: 'Added to exclusive campaign', description: `${o.title}${o.type === 'pending' ? ' — offer not attached yet' : ` (${o.summary})`}`, ts };
                }));
            } catch { /* non-blocking */ }
        })();
        return () => { cancel = true; };
    }, [mobile]);

    // If the customer record has no street address (common for backfilled
    // customers), pull it from their most recent order (one read — the full
    // street lives on orders/{key}/customer, not on the order snapshot).
    useEffect(() => {
        if (!customer) { setOrderAddress(null); return; }
        const hasStreet = customer.address
            || (Array.isArray(customer.addresses) && customer.addresses.some(a => a && (a.fullAddress || a.address)));
        if (hasStreet) { setOrderAddress(null); return; }
        const ords = Array.isArray(customer.orders) ? customer.orders : Object.values(customer.orders || {});
        const target = [...ords]
            .sort((a, b) => new Date(b.orderDate || b.createdAt || 0) - new Date(a.orderDate || a.createdAt || 0))
            .find(o => o && (o.firebaseOrderKey || o.orderId));
        if (!target) { setOrderAddress(null); return; }
        let active = true;
        get(ref(db, `orders/${target.firebaseOrderKey || target.orderId}`)).then(snap => {
            if (!active || !snap.exists()) return;
            const c = snap.val().customer || {};
            if (c.address || c.pincode) setOrderAddress({ fullAddress: c.address || '', city: c.city || '', state: c.state || '', pincode: c.pincode || '' });
        }).catch(() => {});
        return () => { active = false; };
    }, [customer]);

    // Add Order via the proper createOrder system (order number, totals, sync).
    const handleProfileAddOrder = useCallback(async (orderData) => {
        setOrderLoading(true);
        try {
            const productsMap = await fetchProducts();
            const agentName = currentUser?.name || currentUser?.displayName || currentUser?.email || 'Telesales';
            const result = await createOrder({ ...orderData, agentName }, productsMap);
            if (!result.success) { alert('Failed to create order: ' + result.error); return; }
            // Link the converting call, if this Add Order came from a "Ordered" call.
            if (pendingCallId) {
                try {
                    const { TelesalesService } = await import('../services/telesalesService');
                    await TelesalesService.linkCallToOrder(mobile, pendingCallId, result.orderId);
                } catch (e) { /* non-fatal */ }
                setPendingCallId(null);
            }
            setIsOrderModalOpen(false);
            // Keep the new order on screen so its COD advance can be set/verified
            // right here instead of being hunted down on a desk later.
            setLastCreatedOrder({ key: result.firebaseKey, order: result.order });
            await fetchCustomer(); // Recent Orders + stats refresh
        } catch (e) {
            alert('Failed to create order: ' + e.message);
        } finally {
            setOrderLoading(false);
        }
    }, [currentUser, mobile, pendingCallId]);

    const fetchCustomer = useCallback(async () => {
        if (!mobile) {
            setError("No customer ID provided");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await CustomerService.getCustomer(mobile);
            if (!data) {
                setError("Customer not found");
            } else {
                setCustomer({ ...data }); // Force new reference
            }
        } catch (error) {
            console.error("Failed to fetch customer", error);
            setError(error.message || "Failed to load profile");
        } finally {
            setLoading(false);
        }
    }, [mobile]);

    useEffect(() => {
        fetchCustomer();
    }, [fetchCustomer]);

    const handleDelete = async () => {
        if (window.confirm("Are you sure you want to delete this customer? This cannot be undone.")) {
            try {
                await CustomerService.deleteCustomer(mobile);
                navigate('/customers');
            } catch (error) {
                alert("Failed to delete customer");
            }
        }
    };

    const handleAddOrder = async (orderData) => {
        setOrderLoading(true);
        try {
            await CustomerService.addOrder(mobile, orderData);
            setIsOrderModalOpen(false);
            fetchCustomer(); // Refresh data
        } catch (error) {
            console.error("Failed to add order", error);
            alert("Failed to add order");
        } finally {
            setOrderLoading(false);
        }
    };

    // The reward ledger, so credits and redemptions appear on the timeline beside orders
    // and complaints rather than only inside the wallet card. Never allowed to block the
    // profile: a rewards outage must not take the customer record down with it.
    const [rewardRows, setRewardRows] = useState([]);
    // Editing identity and editing an address are separate modals: one is who they are,
    // the other is where a parcel goes, and they are corrected at different moments.
    const [editingProfile, setEditingProfile] = useState(false);
    const [addressModal, setAddressModal] = useState(null);   // { index, address } | { index: -1 }
    useEffect(() => {
        let cancelled = false;
        if (!mobile) { setRewardRows([]); return undefined; }
        rewardsService.getLedger(mobile, 200)
            .then(r => { if (!cancelled) setRewardRows(r.rows || []); })
            .catch(() => { if (!cancelled) setRewardRows([]); });
        return () => { cancelled = true; };
    }, [mobile]);

    // --- Addresses ---
    // Editing now happens in EditAddressModal, which validates with the same pure module
    // the save path uses. These two actions stay here because they act on the LIST rather
    // than on one address.
    const [savingAddress, setSavingAddress] = useState(false);

    const handleSetPrimaryAddress = async (index) => {
        setSavingAddress(true);
        try {
            await CustomerService.setPrimaryAddress(mobile, index, { actor: currentUser?.email || 'staff' });
            fetchCustomer();
        } catch (e) {
            alert(`Could not change the primary address: ${e.message}`);
        } finally { setSavingAddress(false); }
    };

    const handleDeleteAddress = async (index, addr) => {
        const line = String(addr?.fullAddress || addr?.address || 'this address').slice(0, 120);
        if (!window.confirm(`Delete this address?

${line}

Orders already shipped to it are not affected.`)) return;
        setSavingAddress(true);
        try {
            await CustomerService.deleteAddress(mobile, index, { actor: currentUser?.email || 'staff' });
            fetchCustomer();
        } catch (e) {
            alert(`Could not delete the address: ${e.message}`);
        } finally { setSavingAddress(false); }
    };

    if (loading) return <div className="flex items-center justify-center h-screen text-slate-400">Loading Profile...</div>;

    if (error) return (
        <div className="flex flex-col items-center justify-center h-screen gap-4">
            <div className="text-red-500 font-bold text-lg">Error: {error}</div>
            <p className="text-slate-500">Please check your internet connection or permissions.</p>
            <Button onClick={() => navigate(-1)}>Back</Button>
        </div>
    );

    if (!customer) return <div className="flex items-center justify-center h-screen text-slate-400">Customer not found</div>;

    // Virtual Profile Check: If it's a virtual profile from referral DB, some fields might be empty
    const isVirtual = customer.isVirtual;
    const externalData = customer.externalData || {};

    const { rfm, healthScore, churnRisk, remarks, patterns, complaints: rawComplaints, orders } = customer;
    
    // Normalize orders to array to prevent .slice and .length crashes
    const safeOrders = Array.isArray(orders) ? orders : Object.values(orders || {});

    // Filter empty complaints (User Request)
    const complaints = rawComplaints ? Object.entries(rawComplaints)
        .filter(([_, c]) => (c.details && c.details.trim()) || (c.no && c.no.trim()) || (c.month && c.month.trim()))
        .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
        : {};

    // Address resolution is shared with the Add Order modal (logic/customerAddress),
    // so what the profile shows and what an order ships to can never disagree.
    const addressList = resolveAddresses(customer, orderAddress);

    return (
        <div className="space-y-6 max-w-7xl mx-auto pb-10">
            <ProfileAddOrderModal
                isOpen={isOrderModalOpen}
                onClose={() => { setIsOrderModalOpen(false); setPendingCallId(null); setReorderItems(null); }}
                customer={customer}
                shipTo={primaryAddress(customer, orderAddress)}
                onSubmit={handleProfileAddOrder}
                loading={orderLoading}
                initialItems={reorderItems}
            />

            {showOrderHistory && (
                <CustomerOrderHistoryModal
                    isOpen={showOrderHistory}
                    onClose={() => setShowOrderHistory(false)}
                    customer={customer}
                    orders={safeOrders}
                    productsMap={productsMap}
                    onView={(o) => { setShowOrderHistory(false); navigate(`/orders?search=${encodeURIComponent(o.orderId || '')}`); }}
                    onReorder={(o) => {
                        const items = (o.items || []).map(it => ({
                            sku: it.sku || it.productId || '', name: resolveOrderProduct({ items: [it] }, productsMap),
                            // Today's product-list price — the order is saved at that, never the old one.
                            basePrice: Number(productsMap?.[it.sku]?.priceAfterTax ?? productsMap?.[it.sku]?.priceMRP ?? it.price ?? 0) || 0, qty: Number(it.quantity ?? it.qty) || 1,
                        })).filter(x => x.sku);
                        setReorderItems(items.length ? items : null);
                        setShowOrderHistory(false);
                        setIsOrderModalOpen(true);
                    }}
                    onComplaint={(o) => { setShowOrderHistory(false); navigate(`/complaints?mobile=${mobile}&orderId=${encodeURIComponent(o.orderId || '')}`); }}
                />
            )}

            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                        <ArrowLeft size={24} className="text-slate-600" />
                    </button>
                    {/* Photograph — a face makes a 111,000-row database feel like people. */}
                    <div className="w-14 h-14 rounded-full bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center border border-slate-200">
                        {customer.photoUrl
                            ? <img src={customer.photoUrl} alt="" className="w-full h-full object-cover" />
                            : <span className="text-xl font-bold text-slate-400">{String(customer.name || '?').trim().charAt(0).toUpperCase()}</span>}
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold text-slate-900">{customer.name}</h1>
                            {/* Labels set by a bulk import, e.g. "TN customer" for the separate Tamil Nadu list. */}
                            {(Array.isArray(customer.customerTags) ? customer.customerTags : Object.values(customer.customerTags || {})).filter(Boolean).map(tag => (
                                <span key={tag} className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-black uppercase rounded-full border border-amber-200">{tag}</span>
                            ))}
                            {externalData.referralCode && (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase rounded-full border border-emerald-200">
                                    Loyalty Registered
                                </span>
                            )}
                            {(() => {
                                const d = daysToBirthday(customer.dateOfBirth);
                                if (d == null || d > 7) return null;
                                return (
                                    <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-black uppercase rounded-full border border-violet-200">
                                        {d === 0 ? 'Birthday today' : `Birthday in ${d}d`}
                                    </span>
                                );
                            })()}
                        </div>
                        <p className="text-slate-500">
                            {customer.primaryMobile} • {customer.city || 'Unknown City'}
                            {externalData.createdAt && <span className="text-purple-600 font-bold ml-1"> • Member since {formatDate(externalData.createdAt)}</span>}
                        </p>
                        {/* Only shown when recorded — an empty row teaches nobody anything. */}
                        {(customer.email || customer.dateOfBirth || customer.secondaryMobile) && (
                            <p className="text-[12px] text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-3">
                                {customer.email && <span>{customer.email}</span>}
                                {customer.dateOfBirth && <span>DOB {formatDate(Date.parse(`${customer.dateOfBirth}T00:00:00+05:30`))}{ageFrom(customer.dateOfBirth) != null ? ` · ${ageFrom(customer.dateOfBirth)} yrs` : ''}</span>}
                                {customer.secondaryMobile && <span>Alt {customer.secondaryMobile}</span>}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex gap-3">
                    <Button variant="secondary" icon={Edit2} onClick={() => setEditingProfile(true)}>Edit Profile</Button>
                    <Button icon={Plus} onClick={() => setIsOrderModalOpen(true)}>New Order</Button>
                    <Button variant="danger" icon={Trash2} onClick={handleDelete}>Delete Customer</Button>
                </div>
            </div>

            {/* Quick Actions (telesales workspace) */}
            <CustomerActionBar customer={customer} currentUser={currentUser} dialing={dialing} on={{
                // "Call Customer" PLACES the call — it used to jump straight to the
                // result form, which meant logging calls nobody had made. The phone
                // rings first; the log form opens only once the dial is away, so the
                // record follows a real conversation. A policy refusal (DND, cooldown,
                // assigned elsewhere) stops here with the server's reason and no form.
                call: () => startCall('manual'),
                addOrder: () => setIsOrderModalOpen(true),
                feedback: () => setShowFeedback(true),
                remark: async () => { const r = window.prompt('Telecaller remark:'); if (r && r.trim()) { try { await TelesalesService.addTelecallerRemark(mobile, r.trim(), 'general', currentUser); fetchCustomer(); } catch (e) { alert(e.message); } } },
                // Creating a follow-up is paperwork, not a call — it opens the form
                // directly and says so, rather than pretending a call took place.
                followUp: () => { setCallAttempt({ placed: false, followUpOnly: true }); setShowCall(true); },
                toggleDnd: async () => {
                    // Flip on screen IMMEDIATELY — the write worked all along, but the
                    // badge waited for a (sometimes slow) refetch, so it looked broken.
                    const next = !isDND(customer);
                    setCustomer(c => ({ ...c, dnd: next }));
                    try { await TelesalesService.setDND(mobile, next, currentUser); fetchCustomer(); }
                    catch (e) { setCustomer(c => ({ ...c, dnd: !next })); alert(e.message); }
                },
                doNotShip: () => setShowDoNotShip(true),
                export: () => {
                    const c = customer; const esc = csvCell;
                    const row = ['name', 'primaryMobile', 'city', 'state', 'totalOrders', 'totalSpent', 'lastOrderDate', 'customerType', 'favoriteProduct', 'healthScore', 'lastTelecallerRemark'];
                    const csv = row.join(',') + '\n' + row.map(k => esc(c[k])).join(',');
                    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' })); a.download = `customer-${mobile}.csv`; a.click();
                },
            }} />

            {/* Call strip — the honest state of the dial that was just requested.
                Shown only while a call is actually in play; the Live Call dock carries
                the call itself, so this stays a one-line status plus the two actions
                the dock does not offer: cancel an unconfirmed dial, and open the full
                log form for the fields the dock's quick outcome does not capture. */}
            {callAttempt?.placed && (
                <div className={`rounded-xl px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm border ${callAttempt.unconfirmed ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
                    <span className="font-semibold">
                        {callAttempt.unconfirmed
                            ? 'Sent to your phone — not confirmed yet.'
                            : `Ringing ${customer?.primaryMobile || customer?.mobile} on your phone.`}
                    </span>
                    {callAttempt.unconfirmed && <span className="text-amber-800">If it never rang, cancel it so you can dial again.</span>}
                    <div className="ml-auto flex items-center gap-2">
                        {callAttempt.unconfirmed && callAttempt.cancel && (
                            <Button size="sm" variant="outline" onClick={async () => { const r = await callAttempt.cancel(); setCallAttempt(r?.acked ? { placed: true, confirmed: true } : null); }}>
                                Cancel call
                            </Button>
                        )}
                        <Button size="sm" variant="secondary" onClick={() => setShowCall(true)}>Log this call</Button>
                        <button type="button" onClick={() => setCallAttempt(null)} className="text-xs font-semibold opacity-70 hover:opacity-100 px-1">Dismiss</button>
                    </div>
                </div>
            )}

            {/* Open call follow-ups on this customer — any agent's, so the promise is
                seen even when the agent who made it is away. */}
            <FollowupDueBanner mobile={customer.primaryMobile || customer.mobile || mobile} refreshKey={customer} />

            {/* Latest telecaller remark */}
            {(() => { const lr = customer.lastTelecallerRemark || (lastTelecallerRemark(customer.telecallerRemarks) || {}).remark; return lr ? (
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-2 text-sm text-indigo-800"><b>Telecaller note:</b> {lr}</div>
            ) : null; })()}

            {showCall && <CallLogModal isOpen={showCall} onClose={() => { setShowCall(false); setCallAttempt(null); }} customer={customer} currentUser={currentUser} attempt={callAttempt} onRetryDial={() => startCall('manual')} onSaved={fetchCustomer} onOrdered={(callId) => { setPendingCallId(callId); setIsOrderModalOpen(true); }} />}
            {showFeedback && <FeedbackCallModal isOpen={showFeedback} onClose={() => setShowFeedback(false)} customer={customer} currentUser={currentUser} onSaved={fetchCustomer} onCreateComplaint={() => navigate('/complaints')} />}
            {showDoNotShip && <DoNotShipModal isOpen={showDoNotShip} onClose={() => setShowDoNotShip(false)} customer={customer} loading={dnsLoading}
                onSubmit={async ({ on, reason, note }) => {
                    setDnsLoading(true);
                    try { await TelesalesService.setDoNotShip(mobile, on, { reason, note }, currentUser); await fetchCustomer(); setShowDoNotShip(false); }
                    catch (e) { alert(e.message); }
                    finally { setDnsLoading(false); }
                }} />}

            {/* Top Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <StatCard label="Total Spent" value={`₹${(Number(customer.totalSpent) || 0).toLocaleString()}`} subtext={`${customer.totalOrders || 0} Orders`} color="text-purple-600" />
                <StatCard
                    label="Referrals"
                    value={externalData.referralCount || 0}
                    subtext={
                        <div className="flex flex-col gap-0.5 mt-1">
                            <span className="font-bold text-blue-600">{externalData.referralCode || "No Code"}</span>
                            {externalData.referrals && (
                                <span className="text-[10px] text-slate-400">
                                    {externalData.referrals.filter(r => r.payoutStatus === 'PAID').length} Paid •
                                    {externalData.referrals.filter(r => r.payoutStatus !== 'PAID').length} Pending
                                </span>
                            )}
                        </div>
                    }
                    color="text-blue-600"
                />
                <StatCard label="Health Score" value={Number(healthScore) || 0} subtext={(Number(healthScore) || 0) > 80 ? 'Excellent' : (Number(healthScore) || 0) > 50 ? 'Good' : 'At Risk'} color={(Number(healthScore) || 0) > 80 ? 'text-green-600' : (Number(healthScore) || 0) > 50 ? 'text-yellow-600' : 'text-red-600'} />
                <StatCard label="Churn Risk" value={churnRisk || 'Unknown'} subtext="Based on activity" color={churnRisk === 'High' ? 'text-red-600' : 'text-slate-600'} />
                <StatCard label="Avg Order Value" value={`₹${Math.round(Number(customer.averageOrderValue) || 0).toLocaleString()}`} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column */}
                <div className="space-y-6 lg:col-span-2">
                    {/* Bank Details Section (from External DB) */}
                    {externalData && (externalData.accountNumber || externalData.bankName) && (
                        <Section title="Bank Information" icon={Landmark}>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-3 bg-slate-50 rounded-lg">
                                    <p className="text-[10px] text-slate-500 uppercase font-bold">Account Number</p>
                                    <p className="text-sm font-mono font-bold text-slate-700 mt-1">{externalData.accountNumber || '---'}</p>
                                </div>
                                <div className="p-3 bg-slate-50 rounded-lg">
                                    <p className="text-[10px] text-slate-500 uppercase font-bold">Bank Name</p>
                                    <p className="text-sm font-bold text-slate-700 mt-1">{externalData.bankName || '---'}</p>
                                </div>
                                <div className="p-3 bg-slate-50 rounded-lg">
                                    <p className="text-[10px] text-slate-500 uppercase font-bold">IFSC Code</p>
                                    <p className="text-sm font-mono font-bold text-slate-700 mt-1">{externalData.ifsc || '---'}</p>
                                </div>
                            </div>
                        </Section>
                    )}

                    {/* Referral History Section */}
                    {externalData.referrals && externalData.referrals.length > 0 && (
                        <Section title="Referral Network & Performance" icon={Users}>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 text-center">
                                    <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Total Referrals</p>
                                    <p className="text-xl font-black text-blue-600">{externalData.referralCount}</p>
                                </div>
                                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100 text-center">
                                    <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Successful</p>
                                    <p className="text-xl font-black text-emerald-600">
                                        {externalData.referrals.filter(r => r.payoutStatus === 'PAID' || r.payoutStatus === 'APPROVED').length}
                                    </p>
                                </div>
                                <div className="p-3 bg-purple-50 rounded-xl border border-purple-100 text-center">
                                    <p className="text-[10px] font-black text-purple-400 uppercase tracking-widest">Total Value</p>
                                    <p className="text-xl font-black text-purple-600">
                                        ₹{externalData.referrals.reduce((sum, r) => sum + (Number(r.orderTotal) || 0), 0).toLocaleString()}
                                    </p>
                                </div>
                                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-center">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Rewards Paid</p>
                                    <p className="text-xl font-black text-slate-800">
                                        ₹{externalData.referrals.reduce((sum, r) => sum + (r.payoutStatus === 'PAID' ? (Number(r.referralAmount) || 0) : 0), 0).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-slate-50 text-slate-500 font-medium">
                                        <tr>
                                            <th className="px-4 py-2">Date</th>
                                            <th className="px-4 py-2">Referee Name</th>
                                            <th className="px-4 py-2">Order Total</th>
                                            <th className="px-4 py-2 text-right">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {externalData.referrals.map((ref, i) => (
                                            <tr key={i} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate(`/customers/${normalizeMobile(ref.customerMobile)}`)}>
                                                <td className="px-4 py-3 text-xs text-slate-500">{formatDate(ref.createdAt)}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-slate-800">{ref.customerName}</span>
                                                        <span className="text-[10px] text-slate-400 font-mono">{ref.customerMobile}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 font-medium">₹{ref.orderTotal?.toLocaleString()}</td>
                                                <td className="px-4 py-3 text-right">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${ref.payoutStatus === 'PAID' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                                        {ref.payoutStatus || 'PENDING'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Section>
                    )}

                    {/* Remarks Section */}
                    <RemarksSection mobile={mobile} remarks={remarks} onUpdate={fetchCustomer} />

                    {/* Complaints Section (legacy complaint-engine) */}
                    {complaints && Object.keys(complaints).length > 0 && (
                        <ComplaintsSection mobile={mobile} complaints={complaints} onUpdate={fetchCustomer} />
                    )}

                    {/* Ticket-based Complaints & Tickets panel */}
                    <CustomerComplaintsPanel mobile={customer.primaryMobile || mobile} customerName={customer.name} />

                    {/* Courier escalations (Part 4) */}
                    <CustomerEscalationsPanel mobile={customer.primaryMobile || mobile} />


                    {/* Addresses Section */}
                    <Section title="Addresses" icon={MapPin}>
                        <div className="flex justify-between items-center mb-4">
                            <h4 className="text-sm font-semibold text-slate-700">Saved Addresses</h4>
                            <button
                                onClick={() => setAddressModal({ index: -1, address: null })}
                                className="text-xs flex items-center gap-1 text-purple-600 font-bold hover:bg-purple-50 px-2 py-1 rounded transition-colors"
                            >
                                <Plus size={14} /> Add New
                            </button>
                        </div>

                        {addressList.length > 0 ? (
                            <div className="space-y-3">
                                {addressList.map((addr, i) => (
                                    <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                                        <div className="flex justify-between items-start gap-3 group">
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-slate-800">{addr.fullAddress || addr.address}</p>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    {[addr.city, addr.state].filter(Boolean).join(', ')}{addr.pincode ? ` - ${addr.pincode}` : ''}
                                                    {addr.country && !/^india$/i.test(addr.country) ? ` · ${addr.country}` : ''}
                                                </p>
                                                {addr.landmark && <p className="text-xs text-slate-400 mt-0.5">Landmark: {addr.landmark}</p>}
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                {addr.label && <span className="text-[10px] font-bold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">{addr.label}</span>}
                                                {i === 0
                                                    ? <span className="text-xs font-bold bg-purple-100 text-purple-700 px-2 py-1 rounded">Primary</span>
                                                    : (
                                                        <button
                                                            onClick={() => handleSetPrimaryAddress(i)}
                                                            className="opacity-0 group-hover:opacity-100 text-[11px] font-bold text-purple-600 px-2 py-1 rounded hover:bg-purple-50 transition-opacity"
                                                            title="Use this as the delivery address"
                                                        >
                                                            Make primary
                                                        </button>
                                                    )}
                                                <button
                                                    onClick={() => setAddressModal({ index: i, address: addr })}
                                                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 rounded text-slate-500 transition-opacity"
                                                    title="Edit this address"
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                                {/* Deleting the only address would leave nowhere to ship to. */}
                                                {addressList.length > 1 && (
                                                    <button
                                                        onClick={() => handleDeleteAddress(i, addr)}
                                                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600 transition-opacity"
                                                        title="Delete this address"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-slate-400 text-sm text-center py-4">No addresses found.</p>
                        )}
                    </Section>

                    {/* The order just placed from this profile — set / verify its
                        COD advance here, rather than hunting for it on a desk. */}
                    {lastCreatedOrder?.key && (
                        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-bold text-emerald-800">
                                    ✓ Order {lastCreatedOrder.order?.id || ''} created
                                </span>
                                <button onClick={() => setLastCreatedOrder(null)} className="text-xs font-bold text-emerald-700 underline">Dismiss</button>
                            </div>
                            <PartialCodStrip orderKey={lastCreatedOrder.key} order={lastCreatedOrder.order} />
                            {/* Collect the money straight away: create the link,
                                then text it from the gateway or the agent's phone. */}
                            {Number(lastCreatedOrder.order?.payment?.total) > 0 && (
                                <PaymentLinkPanel
                                    orderKey={lastCreatedOrder.key}
                                    orderId={lastCreatedOrder.order?.id}
                                    total={lastCreatedOrder.order?.payment?.total}
                                    mobile={customer?.primaryMobile || customer?.mobile}
                                    agentName={currentUser?.name || currentUser?.displayName || 'Agent'}
                                />
                            )}
                        </div>
                    )}

                    {/* Order History */}
                    <Section title="Recent Orders" icon={ShoppingBag}>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-50 text-slate-500 font-medium">
                                    <tr>
                                        <th className="px-4 py-2">Date</th>
                                        <th className="px-4 py-2">Order ID</th>
                                        <th className="px-4 py-2">Product</th>
                                        <th className="px-4 py-2">SKU</th>
                                        <th className="px-4 py-2">Courier</th>
                                        <th className="px-4 py-2">Delivery Status</th>
                                        <th className="px-4 py-2 text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {safeOrders.slice(0, 5).map((order, i) => {
                                        const skus = Array.isArray(order.items)
                                            ? order.items.map(it => it.sku || it.productId).filter(Boolean)
                                            : [];
                                        return (
                                        <tr key={i} className="hover:bg-slate-50">
                                            <td className="px-4 py-3">{formatDate(order.orderDate || order.createdAt)}</td>
                                            <td className="px-4 py-3 font-mono text-xs">{order.orderId}</td>
                                            <td className="px-4 py-3 truncate max-w-[200px]">
                                                {resolveOrderProduct(order, productsMap)}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-[11px] text-slate-500">
                                                {skus.length ? skus.join(', ') : <span className="text-slate-300">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-xs">
                                                {order.courier ? (
                                                    <div className="text-slate-600">
                                                        {order.courier}
                                                        {order.awb && <div className="text-[10px] text-slate-400 font-mono">{order.awb}</div>}
                                                    </div>
                                                ) : <span className="text-slate-300">—</span>}
                                            </td>
                                            <td className="px-4 py-3">
                                                {order.status && order.status !== 'Pending' ? (
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${order.status.toLowerCase().includes('deliver') ? 'bg-green-100 text-green-700' : order.status.toLowerCase().includes('return') || order.status.toLowerCase().includes('rto') ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                                        {order.status}
                                                    </span>
                                                ) : <span className="text-xs text-slate-400">Pending</span>}
                                            </td>
                                            <td className="px-4 py-3 text-right font-medium">₹{order.amount}</td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {safeOrders.length > 5 && (
                                <div className="mt-4 text-center">
                                    <button onClick={() => setShowOrderHistory(true)} className="text-purple-600 text-sm font-medium hover:underline">View All {safeOrders.length} Orders</button>
                                </div>
                            )}
                        </div>
                    </Section>

                    {/* Order Patterns */}
                    <Section title="Order Intelligence" icon={TrendingUp}>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-slate-500">Average Gap</p>
                                <p className="font-medium">{patterns?.averageGap || 0} days</p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Regularity</p>
                                <p className="font-medium">{patterns?.isRegular ? 'Regular Buyer' : 'Irregular'}</p>
                            </div>
                            <div className="col-span-2">
                                <p className="text-sm text-slate-500 mb-2">Top Products</p>
                                <div className="flex flex-wrap gap-2">
                                    {patterns?.productFrequency && Object.entries(patterns.productFrequency)
                                        .sort(([, a], [, b]) => b - a)
                                        .slice(0, 5)
                                        .map(([prod, count], i) => (
                                            <div key={i} className="flex items-center justify-between w-full bg-slate-50 p-2 rounded">
                                                <span className="text-sm truncate flex-1">{prod}</span>
                                                <span className="text-xs font-bold bg-white px-2 py-1 rounded border shadow-sm">{count}x</span>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </Section>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                    {/* Reward wallet — above RFM, because an agent on a call needs the
                        points balance before they need a segmentation score. */}
                    <CustomerWalletCard mobile={mobile} />

                    {/* RFM Analysis */}
                    <Section title="RFM Analysis" icon={Activity}>
                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-slate-600">Recency</span>
                                    <span className="font-bold">{rfm?.recencyScore || 0}/5</span>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-blue-500" style={{ width: `${(rfm?.recencyScore / 5) * 100}%` }}></div>
                                </div>
                            </div>
                            <div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-slate-600">Frequency</span>
                                    <span className="font-bold">{rfm?.frequencyScore || 0}/5</span>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-purple-500" style={{ width: `${(rfm?.frequencyScore / 5) * 100}%` }}></div>
                                </div>
                            </div>
                            <div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-slate-600">Monetary</span>
                                    <span className="font-bold">{rfm?.monetaryScore || 0}/5</span>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-green-500" style={{ width: `${(rfm?.monetaryScore / 5) * 100}%` }}></div>
                                </div>
                            </div>
                            <div className="pt-4 border-t border-slate-100">
                                <p className="text-center text-sm text-slate-500">Overall RFM Score</p>
                                <p className="text-center text-3xl font-bold text-slate-800">{rfm?.score || 0}</p>
                            </div>
                        </div>
                    </Section>

                    {/* Complaints */}
                    {complaints && Object.keys(complaints).length > 0 && (
                        <Section title="Complaints" icon={AlertTriangle}>
                            <div className="text-center py-4">
                                <p className="text-4xl font-bold text-slate-800">{Object.keys(complaints).length}</p>
                                <p className="text-sm text-slate-500">Total Complaints</p>
                            </div>
                        </Section>
                    )}

                    {/* Timeline */}
                    <CustomerTimeline
                        timeline={customer.timeline}
                        orders={safeOrders}
                        remarks={remarks}
                        complaints={complaints}
                        referrals={externalData.referrals}
                        offerRows={offerRows}
                        rewardRows={rewardRows}
                        resolveName={staffNameFor}
                    />

                    {/* Stock Predictions */}
                    <Section title="Stock Risk" icon={Clock}>
                        <div className="space-y-3">
                            {customer.stockPredictions && customer.stockPredictions.length > 0 ? (
                                customer.stockPredictions.map((pred, i) => (
                                    <div key={i} className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                                        <div className="flex justify-between items-start">
                                            <p className="text-sm font-medium text-orange-800">{pred.product}</p>
                                            <span className="text-xs font-bold bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded">High Risk</span>
                                        </div>
                                        <p className="text-xs text-orange-600 mt-1">Velocity: {pred.velocity.toFixed(2)} units/day</p>
                                    </div>
                                ))
                            ) : (
                                <p className="text-slate-400 text-sm text-center">No immediate stock risks identified for this customer's preferences.</p>
                            )}
                        </div>
                    </Section>
                </div>
            </div>

            {editingProfile && (
                <EditCustomerModal
                    customer={customer}
                    mobile={customer.primaryMobile || mobile}
                    actor={currentUser?.email || 'staff'}
                    onClose={() => setEditingProfile(false)}
                    onSaved={fetchCustomer}
                />
            )}

            {addressModal && (
                <EditAddressModal
                    mobile={customer.primaryMobile || mobile}
                    index={addressModal.index}
                    address={addressModal.address}
                    actor={currentUser?.email || 'staff'}
                    onClose={() => setAddressModal(null)}
                    onSaved={fetchCustomer}
                />
            )}

            {/* Version Indicator for Cache Clearing Verification */}
            <div className="text-[10px] text-slate-300 text-center mt-10 font-mono">
                v1.2.1-enriched • Unified Mobile Format
            </div>
        </div>
    );
};

export default CustomerProfile;
